/** @format */

import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Header,
  Segment,
  Form,
  Message,
  Icon,
  Dimmer,
  Loader,
} from 'semantic-ui-react';
import { getCurrentUserToken } from '../../lib/auth';
import { AI_BASE_URL } from '../../lib/variables';
import './AI.css';

export const AI = () => {
  // Chat state
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [error, setError] = useState('');

  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      const container = messagesEndRef.current.parentElement;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingContent]);

  // Add body padding to avoid footer overlap
  useEffect(() => {
    document.body.style.paddingBottom = '80px';
    return () => {
      document.body.style.paddingBottom = '';
    };
  }, []);

  // Initialize welcome message
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([
        {
          id: 1,
          type: 'assistant',
          content: 'Ciao! Sono il tuo assistente AI. Come posso aiutarti oggi?',
          timestamp: new Date(),
          isMarkdown: false,
        },
      ]);
    }
  }, [messages.length]);

  // Chat functions
  const sendMessage = async e => {
    e.preventDefault();

    const authToken = getCurrentUserToken();
    if (!currentMessage.trim() || isStreaming || !authToken) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: currentMessage.trim(),
      timestamp: new Date(),
      isMarkdown: false,
    };

    setMessages(prev => [...prev, userMessage]);
    const messageToSend = currentMessage.trim();
    setCurrentMessage('');
    setIsStreaming(true);
    setStreamingContent('');
    setError('');

    // Return focus to textarea
    setTimeout(() => {
      const textareaElement = document.querySelector('.ai-input textarea');
      if (textareaElement) {
        textareaElement.focus();
      }
    }, 100);

    try {
      const response = await fetch(`${AI_BASE_URL}/api/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: authToken,
        },
        body: JSON.stringify({ message: messageToSend }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = '';

      const pump = async () => {
        const { done, value } = await reader.read();

        if (done) {
          // Stream completed - add final message
          const aiMessage = {
            id: Date.now() + 1,
            type: 'assistant',
            content: accumulatedText,
            timestamp: new Date(),
            isMarkdown: true,
          };

          setMessages(prev => [...prev, aiMessage]);
          setStreamingContent('');
          setIsStreaming(false);
          return;
        }

        // Decode new text and accumulate
        const newText = decoder.decode(value, { stream: true });
        accumulatedText += newText;
        setStreamingContent(accumulatedText);

        await pump();
      };

      await pump();
    } catch (streamErr) {
      setError(streamErr.message);
      setIsStreaming(false);
      setStreamingContent('');
    }
  };

  const clearResponse = () => {
    if (isStreaming) return;
    setMessages([
      {
        id: 1,
        type: 'assistant',
        content: 'Chat cancellata. Come posso aiutarti?',
        timestamp: new Date(),
        isMarkdown: false,
      },
    ]);
    setStreamingContent('');
    setError('');
  };

  // Utility functions
  const formatTime = timestamp => {
    return timestamp.toLocaleTimeString('it-IT', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderMarkdown = content => {
    // Basic markdown rendering without external libraries
    const lines = content.split('\n');
    return lines.map((line, index) => {
      if (line.startsWith('# ')) {
        return <h3 key={index}>{line.substring(2)}</h3>;
      }
      if (line.startsWith('## ')) {
        return <h4 key={index}>{line.substring(3)}</h4>;
      }
      if (line.startsWith('- ')) {
        return (
          <li key={index} style={{ marginLeft: '20px' }}>
            {line.substring(2)}
          </li>
        );
      }
      if (line.startsWith('**') && line.endsWith('**')) {
        return <strong key={index}>{line.slice(2, -2)}</strong>;
      }
      if (line.trim() === '') {
        return <br key={index} />;
      }
      return <div key={index}>{line}</div>;
    });
  };

  // Render chat interface
  return (
    <Container fluid className="ai-chat-container">
      <Header attached="top" className="ai-header">
        {/* Error message */}
        {error && (
          <Message negative>
            <Icon name="exclamation triangle" />
            Errore: {error}
          </Message>
        )}
      </Header>

      <Segment attached className="ai-chat-messages">
        <div className="messages-container">
          {messages.map(message => (
            <div key={message.id} className={`message-wrapper ${message.type}`}>
              <Message
                className={`chat-message ${message.type}`}
                size="small"
                compact
              >
                <div className="message-content">
                  {message.isMarkdown
                    ? renderMarkdown(message.content)
                    : message.content}
                </div>
                <div className="message-time">
                  {formatTime(message.timestamp)}
                </div>
              </Message>
            </div>
          ))}

          {/* Streaming message */}
          {isStreaming && streamingContent && (
            <div className="message-wrapper assistant">
              <Message className="chat-message assistant streaming">
                <div className="message-content">
                  {renderMarkdown(streamingContent)}
                </div>
                <div className="message-time">
                  <Icon name="circle notched" loading />
                  Streaming...
                </div>
              </Message>
            </div>
          )}

          {/* Loading indicator */}
          {isStreaming && !streamingContent && (
            <div className="message-wrapper assistant">
              <Message className="chat-message assistant loading">
                <Dimmer active inverted>
                  <Loader size="mini">Gemini sta pensando...</Loader>
                </Dimmer>
                <div style={{ height: '40px' }} />
              </Message>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </Segment>

      <Segment attached="bottom" className="ai-input-section" basic>
        <Form onSubmit={sendMessage}>
          <Form.Group widths="equal">
            <Form.TextArea
              ref={inputRef}
              placeholder="Scrivi il tuo messaggio... (Ctrl+Enter per inviare)"
              value={currentMessage}
              onChange={e => setCurrentMessage(e.target.value)}
              onKeyDown={e => {
                if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                  e.preventDefault();
                  sendMessage(e);
                }
              }}
              disabled={isStreaming}
              className="ai-input"
              rows={3}
              style={{ resize: 'vertical', minHeight: '80px' }}
            />
          </Form.Group>
          <Form.Group>
            <Form.Button
              type="submit"
              primary
              disabled={!currentMessage.trim() || isStreaming}
              loading={isStreaming}
              icon="send"
              content={isStreaming ? 'Generando...' : 'Invia (Ctrl+Enter)'}
              size="small"
              floated="right"
            />
            <Form.Button
              basic
              onClick={clearResponse}
              title="Pulisci chat"
              disabled={isStreaming}
            >
              <Icon name="eraser" />
              Pulisci
            </Form.Button>
          </Form.Group>
        </Form>
      </Segment>
    </Container>
  );
};

export default { AI };
